/**
 * AuthStoreHydrator
 *
 * Single place for the auth bootstrap sequence.
 * Mounted once in App.jsx (inside Router, wrapping the rest of the tree).
 *
 * ## Auth responsibilities
 *
 * 1. On mount: read storage → set token / policy_pending / unauthenticated.
 * 2. React to authToken changes: decode JWT, fetch user data.
 * 3. React to userData changes: set authBootstrapStatus to authenticated.
 * 4. Wire useInactivityLogout (10 min inactivity timeout).
 *
 * ## Branding responsibilities
 *
 * 1. Fetch public login branding while unauthenticated.
 * 2. Fetch authenticated app branding only after policy/auth bootstrap completes.
 * 3. Cancel requests when auth state changes and clear app branding on logout.
 *
 * ## Preload responsibilities (after authenticated, one-shot latches)
 *
 * | Phase | Function | Blocks UI? |
 * | --- | --- | --- |
 * | Mount (cold refresh) | `preloadNamespacesForRoute(current path)` | No — parallel with JWT fetch |
 * | Authenticated | `preloadNamespacesForRoute` + `preloadRemainingNamespaces` | No — background i18n |
 * | Authenticated | `preloadPostLoginModuleChunks` | No — background JS queue |
 *
 * @see docs/Auth/AuthStoreHydrator.md
 * @see docs/frontend-branding.md
 * @see docs/runtime-preload.md
 */
import { useEffect, useRef, useCallback } from "react";
import { getUserData } from "api/services/usersServices";
import { logout, getAuthenticatedEntryPath } from "auth";
import { mapApiUserToAuthUserData } from "auth/userData";
import jwt_decode from "jwt-decode";
import useAuthStore from "stores/useAuthStore";
import useBrandingStore, { BRANDING_STATUS } from "stores/useBrandingStore";
import useInactivityLogout from "hooks/useInactivityLogout";
import { getRouteVariantSnapshot } from "hooks/useMobileViewport";
import { isPublicRoute, preloadNamespacesForRoute, preloadRemainingNamespaces } from "i18n";
import { preloadPostLoginModuleChunks } from "routes/routeChunkLoaders";
import { preloadRouteDestination } from "routes/routePreloaders";
import { clearSentryUser, setSentryUser } from "observability/sentry";
import { fetchPostLoginBranding, fetchPreLoginBranding } from "api/services/brandingServices";

// Store access
const store = useAuthStore.getState();
const brandingStore = useBrandingStore.getState();

export default function AuthStoreHydrator({ children }) {
  const hasBootstrapped = useRef(false);
  const lastFetchedUserIdRef = useRef(null);
  /** One-shot latch: background i18n warmup after first `authenticated` transition. */
  const hasStartedAuthenticatedNamespaceWarmupRef = useRef(false);
  /** One-shot latch: sequential JS chunk queue after first `authenticated` + userData. */
  const hasStartedAuthenticatedChunkWarmupRef = useRef(false);
  const authToken = useAuthStore((s) => s.authToken);
  const initialAuthToken = useAuthStore((s) => s.initialAuthToken);
  const userData = useAuthStore((s) => s.userData);
  const authBootstrapStatus = useAuthStore((s) => s.authBootstrapStatus);

  useInactivityLogout({
    isAuthenticated: !!authToken,
    logout: () => logout({ setExpired: true }),
  });

  const fetchUserData = useCallback(async (userId, decodedTokenData) => {
    let response;

    try {
      response = await getUserData(userId);
    } catch {
      const idFromToken = decodedTokenData?._id || decodedTokenData?.id;
      return {
        fetchedUserData: {
          ...(idFromToken && { id: idFromToken }),
          ...(decodedTokenData.sub && { email: decodedTokenData.sub }),
          ...(decodedTokenData.role && { role: decodedTokenData.role }),
        },
        initialUserData: null,
      };
    }

    return {
      fetchedUserData: mapApiUserToAuthUserData(response),
      initialUserData: initialAuthToken === authToken ? decodedTokenData : jwt_decode(initialAuthToken),
    };
  }, [initialAuthToken, authToken]);

  // ── Step 1-3: Bootstrap on mount ──
  useEffect(() => {
    if (hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    store.setAuthBootstrapStatus("bootstrapping");

    const token = localStorage.getItem("token");
    const initialToken = localStorage.getItem("initial_token");
    const pendingToken = localStorage.getItem("pending_token");

    if (token || initialToken) {
      // Hard refresh with session — preload current route i18n while JWT/user fetch runs.
      void preloadNamespacesForRoute(globalThis.location.pathname);
    } else if (pendingToken) {
      // Policy gate only needs terms namespace before consent UI renders.
      void preloadNamespacesForRoute("/terms-and-conditions");
    } else if (isPublicRoute(globalThis.location.pathname)) {
      // Public session links (e.g. /session/obligation/:token) have no JWT — preload route i18n here.
      void preloadNamespacesForRoute(globalThis.location.pathname);
    }

    if (token) {
      store.setAuthToken(token);
      if (initialToken) store.setInitialAuthToken(initialToken);
      return;
    }

    if (pendingToken) {
      store.setAuthBootstrapStatus("policy_pending");
      return;
    }

    store.clearSession({ keepReturnUrl: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 4: Token decode and user data fetching ──
  useEffect(() => {
    if (!authToken) {
      lastFetchedUserIdRef.current = null;
      return;
    }

    if (authBootstrapStatus === "policy_pending") return;

    let decodedTokenData;
    try {
      decodedTokenData = jwt_decode(authToken);
    } catch {
      store.clearSession({ keepReturnUrl: true });
      return;
    }

    const userId = decodedTokenData?._id || decodedTokenData?.id;
    if (!userId) {
      store.clearSession({ keepReturnUrl: true });
      return;
    }

    if (lastFetchedUserIdRef.current === userId) return;
    lastFetchedUserIdRef.current = userId;

    fetchUserData(userId, decodedTokenData)
      .then(({ fetchedUserData, initialUserData }) => {
        store.setUserData(fetchedUserData);
        if (initialUserData) store.setInitialUserData(initialUserData);
      })
      .catch((err) => {
        console.error(err);
        const idFromToken = decodedTokenData?._id || decodedTokenData?.id;
        if (idFromToken) {
          store.setUserData({
            id: idFromToken,
            ...(decodedTokenData.sub && { email: decodedTokenData.sub }),
            ...(decodedTokenData.role && { role: decodedTokenData.role }),
          });
        } else {
          store.clearSession({ keepReturnUrl: true });
        }
      });
  }, [authToken, authBootstrapStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 5: Final authentication after userData is set ──
  useEffect(() => {
    if (!userData || authBootstrapStatus === "policy_pending") return;

    if (!userData?.id) {
      store.clearSession({ keepReturnUrl: true });
      return;
    }

    store.setAuthBootstrapStatus("authenticated");
  }, [userData, authBootstrapStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sentry receives only an opaque user ID and role after the full auth
  // bootstrap succeeds. All other states clear context immediately.
  useEffect(() => {
    if (authBootstrapStatus === "authenticated" && userData?.id) {
      setSentryUser(userData);
      return;
    }

    clearSentryUser();
  }, [authBootstrapStatus, userData]);

  useEffect(() => {
    if (authBootstrapStatus !== "unauthenticated") return undefined;

    const controller = new AbortController();

    brandingStore.setPreLoginStatus(BRANDING_STATUS.LOADING);
    fetchPreLoginBranding({ signal: controller.signal })
      .then((payload) => {
        if (controller.signal.aborted) return;
        brandingStore.setPreLoginBranding(payload);
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === "AbortError") return;
        console.error("Pre-login branding fetch failed:", error);
        brandingStore.setPreLoginStatus(BRANDING_STATUS.ERROR, error?.message || "Pre-login branding fetch failed");
      });

    return () => controller.abort();
  }, [authBootstrapStatus]);

  // App branding is session-scoped. Cancellation prevents a response from a
  // previous session being written after logout or another auth transition.
  useEffect(() => {
    if (authBootstrapStatus !== "authenticated") {
      if (authBootstrapStatus === "unauthenticated") {
        // Keep persisted cache during bootstrapping, but never across logout.
        brandingStore.resetPostLogin();
      }
      return undefined;
    }

    const controller = new AbortController();

    brandingStore.setPostLoginStatus(BRANDING_STATUS.LOADING);
    fetchPostLoginBranding({ signal: controller.signal })
      .then((payload) => {
        if (controller.signal.aborted) return;
        brandingStore.setPostLoginBranding(payload);
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === "AbortError") return;
        console.error("Post-login branding fetch failed:", error);
        brandingStore.setPostLoginStatus(BRANDING_STATUS.ERROR, error?.message || "Post-login branding fetch failed");
      });

    return () => controller.abort();
  }, [authBootstrapStatus]);

  useEffect(() => {
    if (authBootstrapStatus !== "authenticated") {
      hasStartedAuthenticatedNamespaceWarmupRef.current = false;
      hasStartedAuthenticatedChunkWarmupRef.current = false;
      return;
    }

    // --- i18n: shell + route gate, then background remaining namespaces ---
    if (!hasStartedAuthenticatedNamespaceWarmupRef.current) {
      hasStartedAuthenticatedNamespaceWarmupRef.current = true;

      void preloadNamespacesForRoute(globalThis.location.pathname).then(() => {
        void preloadRemainingNamespaces();
      });
    }

    // --- JS chunks: access-filtered queue; separate from i18n gate ---
    if (!hasStartedAuthenticatedChunkWarmupRef.current && userData?.id) {
      hasStartedAuthenticatedChunkWarmupRef.current = true;
      const { isMobileViewport } = getRouteVariantSnapshot();
      const variant = isMobileViewport ? "mobile" : "desktop";
      const currentPathname = typeof globalThis !== "undefined" ? globalThis.location.pathname : undefined;
      const preloadLandingPath = getAuthenticatedEntryPath(userData, currentPathname);

      void preloadRouteDestination(preloadLandingPath, userData, {
        variant,
        requireAccess: false,
      });

      void preloadPostLoginModuleChunks(userData, {
        variant,
        currentPathname: preloadLandingPath,
      });
    }
  }, [authBootstrapStatus, userData]);

  return children;
};
